export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isOffline(): boolean {
    return this.status === 0;
  }

  get isConflict(): boolean {
    return this.status === 412 || this.status === 409;
  }
}

export interface ApiResponse<T> {
  data: T;
  /** Blob ETag, echoed back on write to detect concurrent edits from other devices. */
  etag: string | null;
}

type TokenProvider = () => Promise<string | null>;

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiClient {
  constructor(private readonly getToken: TokenProvider) {}

  get<T>(path: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, { signal });
  }

  post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, { body, signal });
  }

  put<T>(path: string, body: unknown, options: WriteOptions = {}): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, options.etag ? { body, ...options } : { body, ...options });
  }

  patch<T>(path: string, body: unknown, options: WriteOptions = {}): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, { body, ...options });
  }

  delete<T>(path: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, { signal });
  }

  private async request<T>(
    method: string,
    path: string,
    options: { body?: unknown; etag?: string | null; signal?: AbortSignal } = {},
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.etag) headers['If-Match'] = options.etag;

    const token = await this.getToken();
    if (token) {
      // Static Web Apps overwrites Authorization with its own platform token
      // before forwarding to a managed function, so carry ours alongside it.
      headers['X-Line-Runner-Token'] = token;
      headers.Authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(`${BASE}/${path}`, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
      });
    } catch (cause) {
      if (options.signal?.aborted) throw cause;
      // Network failure is expected offline; status 0 lets callers fall back to IndexedDB.
      throw new ApiError(0, 'You appear to be offline.');
    }

    const etag = response.headers.get('ETag');
    const payload = await readBody(response);

    if (!response.ok) {
      const message =
        (isRecord(payload) && typeof payload.error === 'string' && payload.error) ||
        `Request failed (${response.status})`;
      throw new ApiError(response.status, message, payload);
    }

    return { data: payload as T, etag };
  }
}

interface WriteOptions {
  etag?: string | null;
  signal?: AbortSignal;
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
